import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { includes } from '@nullvoxpopuli/ember-composable-helpers';
import BasicDropdown from 'ember-basic-dropdown/components/basic-dropdown';
import EmberTooltip from 'ember-tooltips/components/ember-tooltip';

interface FilterOption {
  label: string;
  value: string;
}

interface HealthDashboardMultiSelectFilterSignature {
  Args: {
    options: FilterOption[];
    selected: FilterOption[];
    onChange: (opts: FilterOption[]) => void;
    placeholder?: string;
  };
}

export default class HealthDashboardMultiSelectFilter extends Component<HealthDashboardMultiSelectFilterSignature> {
  get labelText(): string {
    const { selected } = this.args;
    const placeholder = this.args.placeholder ?? 'Select...';
    if (selected.length === 0) return placeholder;
    if (selected.length === 1) return selected[0]!.label;
    return `${selected.length} selected`;
  }

  get tooltipText(): string {
    const { selected } = this.args;
    if (selected.length === 0) return this.args.placeholder ?? 'Select...';
    return selected.map((s) => s.label).join(', ');
  }

  get selectedValues(): string[] {
    return this.args.selected.map((s) => s.value);
  }

  @action
  toggle(opt: FilterOption): void {
    const isSelected = this.selectedValues.includes(opt.value);
    if (isSelected) {
      this.args.onChange(this.args.selected.filter((s) => s.value !== opt.value));
    } else {
      this.args.onChange([...this.args.selected, opt]);
    }
  }

  <template>
    <BasicDropdown @matchTriggerWidth={{true}} @verticalPosition="below" as |dropdown|>
      <dropdown.Trigger class="ember-power-select-trigger w-full">
        <span class="block truncate pr-5">{{this.labelText}}</span>
        <EmberTooltip @side="top" @text={{this.tooltipText}} />
      </dropdown.Trigger>
      <dropdown.Content>
        <div class="ember-power-select-dropdown ember-power-select-dropdown--below py-2 max-h-[50vh] overflow-auto">
          <div class="ember-power-select-options !max-h-full">
            {{#each @options as |opt|}}
              {{#let (includes opt.value this.selectedValues) as |isSelected|}}
                <label
                  class="flex items-center space-x-1 ember-power-select-option
                    {{if isSelected '!text-gem-60 selected' '!text-carbon-70'}}"
                >
                  <input type="checkbox" checked={{isSelected}} {{on "change" (fn this.toggle opt)}} />
                  <p class="text-base mb-[-2px]">{{opt.label}}</p>
                </label>
              {{/let}}
            {{/each}}
          </div>
        </div>
      </dropdown.Content>
    </BasicDropdown>
  </template>
}
